/**
 * UAT-B2 (frame 1d) — the ONE Today resurface of the skipped guardian-invite
 * beat. Pure render-condition so the no-nagging rules are unit-pinnable.
 *
 * Render iff ALL hold:
 *   - the athlete is a derived minor (auto-retires when age leaves 13–15)
 *   - no relationship rows exist (auto-retires once any guardian is linked)
 *   - the invite beat was skipped (`_guardian_invite_skipped_at` present)
 *   - "Not now" has NOT been pressed (`_guardian_nudge_dismissed_at` absent —
 *     once dismissed, the card never returns)
 *   - a LATER CALENDAR DAY than the skip: never on the skip's own day, so a
 *     same-visit page reload does NOT re-arm it (F-510). The ratified intent is
 *     "next sign-in at the earliest"; calendar-day is the reload-stable proxy —
 *     the earliest it can resurface is the day after the skip.
 */
export interface GuardianNudgeInput {
  derivedMinor: boolean;
  hasAnyRelationship: boolean;
  skippedAt: string | null | undefined;
  dismissedAt: string | null | undefined;
  /** Current time (ISO). The skip must fall on an EARLIER calendar day. */
  nowIso: string;
}

/** Local-time calendar day (YYYY-MM-DD) of an ISO instant. */
function localCalendarDay(iso: string): string | null {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * UAT-B2 — the onboarding invite-beat age band: 13–15 inclusive. A KNOWN <13 is
 * on the untouched COPPA hard-gate path; 16+ is adult-track (optional guardian)
 * and skips the beat. `null` (unknown age) never triggers the beat.
 */
export function isGuardianBeatBand(age: number | null): boolean {
  return age != null && age >= 13 && age <= 15;
}

export function shouldResurfaceGuardianNudge(input: GuardianNudgeInput): boolean {
  const { derivedMinor, hasAnyRelationship, skippedAt, dismissedAt, nowIso } = input;
  if (!derivedMinor) return false;
  if (hasAnyRelationship) return false;
  if (!skippedAt) return false;
  if (dismissedAt) return false;
  const skipDay = localCalendarDay(skippedAt);
  const nowDay = localCalendarDay(nowIso);
  if (skipDay === null || nowDay === null) return false;
  // Strictly earlier calendar day → a same-day reload never re-arms; the day
  // after the skip (or later) does. (YYYY-MM-DD compares lexicographically.)
  return skipDay < nowDay;
}
